import { Clock, Shield, MapPin, Award, Phone, Star, Heart, Users, CheckCircle } from "lucide-react";

const TrustSection = () => {
  const stats = [
    { value: "15", unit: "min", label: "Average Response Time" },
    { value: "24/7", unit: "", label: "Emergency Availability" },
    { value: "10+", unit: "yrs", label: "Experience" },
    { value: "5000+", unit: "", label: "Satisfied Customers" },
  ];

  const testimonials = [
    {
      name: "Sonja Wood",
      location: "Saskatoon",
      rating: 5,
      text: "Thanks so much for helping this Sr. Citizen with your emergency service. You're great! Please have a meal on me to say thanks!",
      isHandwritten: true,
      image: "https://customer-assets.emergentagent.com/job_emergency-locksmith-4/artifacts/7fgpff8t_WhatsApp%20Image%202026-04-14%20at%2010.53.26%20PM.jpeg",
    },
    {
      name: "David R.",
      location: "Stonebridge, Saskatoon",
      rating: 5,
      text: "Got locked out of my house at 11pm with my kids inside. Called 306 Locksmith and they were at my door in under 20 minutes. Professional and didn't overcharge for the late hour. Will definitely call again.",
    },
    {
      name: "Prairie Auto Shop",
      location: "Saskatoon",
      rating: 5,
      text: "We use 306 Locksmith for all our dealership key programming. They're reliable, fairly priced, and always available when we need them. Great local business.",
    },
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-slate-900" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Family Owned Banner */}
        <div className="bg-slate-800 border border-slate-700 rounded-sm p-8 md:p-12 mb-12 text-center" data-testid="family-owned-banner">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-amber-400" />
            <span className="text-amber-400 text-sm uppercase tracking-[0.2em] font-semibold">
              Proudly Serving Saskatoon
            </span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
            Local Family-Owned Business
          </h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-6">
            We're your neighbours, and we treat every customer like family. 
            When you call 306 Locksmith, you're supporting a local business that truly cares about our community.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-white">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-400" />
              <span>Proudly Local</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              <span>Honest & Fair Pricing</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              <span>Always Here When You Need Us</span>
            </div>
          </div>
        </div>

        {/* Trust Badges Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          <div className="bg-blue-900/50 border border-blue-700 rounded-sm p-6 text-center">
            <Heart className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-1">Senior Friendly</h3>
            <p className="text-slate-400 text-sm">Patient & respectful service</p>
          </div>
          <div className="bg-blue-900/50 border border-blue-700 rounded-sm p-6 text-center">
            <Users className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-1">Family Trusted</h3>
            <p className="text-slate-400 text-sm">Serving families since 2014</p>
          </div>
          <div className="bg-blue-900/50 border border-blue-700 rounded-sm p-6 text-center">
            <Shield className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-1">100% Guaranteed</h3>
            <p className="text-slate-400 text-sm">Satisfaction or money back</p>
          </div>
          <div className="bg-blue-900/50 border border-blue-700 rounded-sm p-6 text-center">
            <CheckCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-1">No Hidden Fees</h3>
            <p className="text-slate-400 text-sm">Price quoted is price paid</p>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 border border-slate-700 bg-slate-800 rounded-sm"
              data-testid="stat-card"
            >
              <div className="font-heading text-4xl md:text-5xl text-white">
                {stat.value}
                <span className="text-amber-500">{stat.unit}</span>
              </div>
              <p className="text-slate-400 mt-2">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Why Choose Us */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-amber-400 mb-4 block">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">
              Saskatoon's Most Trusted Locksmith
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              306 Locksmith has been serving Saskatoon and surrounding communities for over a decade. Our team of licensed professionals is committed to providing fast, reliable, and affordable locksmith services.
            </p>

            <div className="space-y-4">
              <TrustPoint
                icon={<Clock className="w-5 h-5" />}
                title="Fastest Response Time"
                description="We guarantee arrival within 15-30 minutes for emergencies"
              />
              <TrustPoint
                icon={<Shield className="w-5 h-5" />}
                title="Licensed & Insured"
                description="All our technicians are fully licensed and bonded"
              />
              <TrustPoint
                icon={<MapPin className="w-5 h-5" />}
                title="Local Experts"
                description="We know Saskatoon and serve the entire surrounding area"
              />
              <TrustPoint
                icon={<Award className="w-5 h-5" />}
                title="Upfront Pricing"
                description="No hidden fees or surprise charges - guaranteed"
              />
            </div>
          </div>

          {/* Service Area Map */}
          <div className="relative">
            <div className="bg-slate-800 rounded-sm p-8 border border-slate-700">
              <h3 className="font-heading text-xl text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-400" />
                Service Areas
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  "Saskatoon",
                  "Warman",
                  "Martensville",
                  "Osler",
                  "Dalmeny",
                  "Dundurn",
                  "Langham",
                  "Clavet",
                  "Aberdeen",
                  "Delisle",
                  "Hepburn",
                  "Colonsay",
                ].map((area) => (
                  <div
                    key={area}
                    className="flex items-center gap-2 text-slate-300"
                  >
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    {area}
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-sm mt-4">
                + All surrounding communities
              </p>
            </div>

            {/* Emergency Call Box */}
            <div className="absolute -bottom-6 -right-6 bg-amber-500 text-slate-900 p-6 rounded-sm shadow-xl">
              <Phone className="w-8 h-8 mb-2 text-slate-900" />
              <p className="text-sm text-slate-700">Emergency Hotline</p>
              <a
                href="tel:3062625995"
                className="font-heading text-2xl text-slate-900 hover:text-blue-900 transition-colors"
              >
                306-262-5995
              </a>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div>
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-amber-400 mb-4 block">
              Testimonials
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl text-white">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustPoint = ({ icon, title, description }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 bg-amber-500/20 rounded-sm flex items-center justify-center text-amber-400 flex-shrink-0">
      {icon}
    </div>
    <div>
      <h4 className="font-semibold text-white">{title}</h4>
      <p className="text-slate-400 text-sm">{description}</p>
    </div>
  </div>
);

const TestimonialCard = ({ testimonial }) => (
  <div
    className={`bg-slate-800 border border-slate-700 rounded-sm p-6 ${testimonial.isHandwritten ? 'md:col-span-2 lg:col-span-1' : ''}`}
    data-testid="testimonial-card"
  >
    {/* Handwritten Note Image */}
    {testimonial.isHandwritten && testimonial.image && (
      <div className="mb-4 rounded-sm overflow-hidden border-2 border-amber-500/50">
        <img 
          src={testimonial.image} 
          alt="Customer thank you note" 
          className="w-full h-auto object-contain"
        />
      </div>
    )}

    {/* Stars */}
    <div className="flex gap-1 mb-4">
      {[...Array(testimonial.rating)].map((_, i) => (
        <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
      ))}
    </div>

    {/* Quote */}
    <p className="text-slate-300 mb-4 italic">"{testimonial.text}"</p>

    {/* Author */}
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-slate-900 font-semibold">
        {testimonial.name.charAt(0)}
      </div>
      <div>
        <p className="font-semibold text-white">{testimonial.name}</p>
        <p className="text-slate-500 text-sm">{testimonial.location}</p>
      </div>
    </div>
  </div>
);

export default TrustSection;
